/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useRef, useState, useCallback } from "react";
import "./ProjectModal.css";
import { ProjectModalContent, ProjectModalImage } from "../../types";
import { getCustomElementUrl, getProjectPictureUrl } from "../../utils/cloudinary";
import { stringToProjectModalImage } from "../../services/projectModalImageService";
import Tooltip from "../tooltip/Tooltip";
import { techLogoToPicture } from "../../services/techLogoService";
import projectsData from "../../static/projects_content.json";

// Cloudinary URLs for images
const samplePicture = getProjectPictureUrl('sample_picture_modal');
const closeIcon = getCustomElementUrl('cross');
const arrowDown = getCustomElementUrl('arrow_down');

interface ProjectModalProps {
    project: ProjectModalContent;
    onClose: () => void;
    onImageClick: (
        imageSrc: string,
        objectFit: "cover" | "contain" | "fill" | "scale-down" | "none",
        descriptiveTitle?: string
    ) => void;
    inFocus: boolean;
    isVisible?: boolean;
}

const ProjectModal = ({
    project,
    onClose,
    onImageClick,
    inFocus,
    isVisible = true,
}: ProjectModalProps) => {
    const containerRef = useRef<HTMLDivElement>(null);
    const rightContainerRef = useRef<HTMLDivElement>(null);
    const fadeTimeoutRef = useRef<NodeJS.Timeout>();
    const [currentImageIndex, setCurrentImageIndex] = useState(0);
    const [isDesktop, setIsDesktop] = useState(window.innerWidth > 1200);
    const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
    const [displayedImage, setDisplayedImage] = useState<ProjectModalImage>(
        project.images.length
            ? {
                  path:
                      stringToProjectModalImage(project.images[0].path) ||
                      samplePicture,
                  objectFit: project.images[0].objectFit,
              }
            : { path: samplePicture, objectFit: "cover" }
    );
    const [isFading, setIsFading] = useState(false);
    const [showImageTooltip, setShowImageTooltip] = useState(false);
    const [showTechTooltip, setShowTechTooltip] = useState(false);
    const [tooltipPosition, setTooltipPosition] = useState({ x: 0, y: 0 });
    const [activeTechLogo, setActiveTechLogo] = useState<string>("");
    const [hasScrolled, setHasScrolled] = useState(false);
    const [isScrollable, setIsScrollable] = useState(false);
    const [isHoveringImage, setIsHoveringImage] = useState(false);
    const lastMousePositionRef = useRef({ x: 0, y: 0 });

    // Find matching project data from projects_content.json
    const matchingProject = projectsData.find(
        (p: { name: string; tech_logos: string[] }) =>
            p.name.toLowerCase().includes(project.title.toLowerCase()) ||
            project.title.toLowerCase().includes(p.name.toLowerCase())
    );

    useEffect(() => {
        return () => {
            if (fadeTimeoutRef.current) {
                clearTimeout(fadeTimeoutRef.current);
            }
        };
    }, []);

    useEffect(() => {
        const handleResize = () => {
            const newIsDesktop = window.innerWidth > 1200;
            const newIsMobile = window.innerWidth <= 768;
            setIsDesktop(newIsDesktop);
            setIsMobile(newIsMobile);
            if (!newIsDesktop) {
                setCurrentImageIndex(0);
                setDisplayedImage(
                    project.images.length
                        ? {
                              path:
                                  stringToProjectModalImage(
                                      project.images[0].path
                                  ) || samplePicture,
                              objectFit: project.images[0].objectFit,
                          }
                        : { path: samplePicture, objectFit: "cover" }
                );
            }
        };
        window.addEventListener("resize", handleResize);
        return () => window.removeEventListener("resize", handleResize);
    }, [project.images]);

    useEffect(() => {
        const rightContainer = rightContainerRef.current;
        if (!rightContainer) return;

        // Check if content is scrollable
        const checkIfScrollable = () => {
            if (rightContainer) {
                setIsScrollable(
                    rightContainer.scrollHeight >
                        rightContainer.clientHeight + 20
                );
            }
        };

        // Run the check initially and on resize
        checkIfScrollable();
        window.addEventListener("resize", checkIfScrollable);

        const handleScroll = () => {
            const { scrollTop } = rightContainer;

            // Set hasScrolled state - works on both mobile and desktop
            if (scrollTop > 10) {
                setHasScrolled(true);
            } else {
                setHasScrolled(false);
            }

            // Only do image change logic on desktop and not for Møvi.com project
            if (isDesktop && project.title !== "Møvi.com") {
                const { scrollHeight, clientHeight } = rightContainer;
                const scrollPercentage =
                    scrollTop / (scrollHeight - clientHeight);
                const numImages = project.images.length;
                if (numImages <= 1) return;
                const newImageIndex = Math.min(
                    Math.floor(scrollPercentage * numImages),
                    numImages - 1
                );
                setCurrentImageIndex(newImageIndex);
            }
        };

        rightContainer.addEventListener("scroll", handleScroll);
        return () => {
            rightContainer.removeEventListener("scroll", handleScroll);
            window.removeEventListener("resize", checkIfScrollable);
        };
    }, [project.images.length, isDesktop, project.title]);

    useEffect(() => {
        if (!isDesktop || project.title === "Møvi.com") return;

        // Create the new image object
        const newImage: ProjectModalImage = {
            path:
                stringToProjectModalImage(
                    project.images[currentImageIndex].path
                ) || samplePicture,
            objectFit: project.images[currentImageIndex].objectFit as
                | "cover"
                | "contain"
                | "fill"
                | "scale-down"
                | "none",
        };

        // If it's the same image, do nothing
        if (newImage.path === displayedImage.path) return;

        // Clear any existing timeout
        if (fadeTimeoutRef.current) {
            clearTimeout(fadeTimeoutRef.current);
        }

        // Create a hidden image element to preload the specific next image
        const imgLoader = new Image();

        // Set fade duration based on project title - longer for Møvi.com
        const fadeDuration = 300; // No longer needed to check for Møvi.com since we return early

        // When the image is loaded, start the transition
        imgLoader.onload = () => {
            setIsFading(true);
            fadeTimeoutRef.current = setTimeout(() => {
                setDisplayedImage(newImage);
                setIsFading(false);
            }, fadeDuration);
        };

        // If image loading fails, still attempt to show it
        imgLoader.onerror = () => {
            console.warn("Failed to preload image:", newImage.path);
            setIsFading(true);
            fadeTimeoutRef.current = setTimeout(() => {
                setDisplayedImage(newImage);
                setIsFading(false);
            }, fadeDuration);
        };

        // Start loading the image
        imgLoader.src = newImage.path;
    }, [
        currentImageIndex,
        displayedImage,
        project.images,
        isDesktop,
        project.title,
    ]);

    // Preload all project images on component mount
    useEffect(() => {
        if (
            !isDesktop ||
            project.images.length <= 1 ||
            project.title === "Møvi.com"
        )
            return;

        // Preload all project images
        project.images.forEach((img) => {
            const imgPath = stringToProjectModalImage(img.path);
            if (imgPath) {
                const preloadImage = new Image();
                preloadImage.src = imgPath;
            }
        });
    }, [project.images, isDesktop, project.title]);

    // Handle mouse movement over the image
    const handleMouseMove = (e: React.MouseEvent<HTMLDivElement>) => {
        const position = {
            x: e.clientX,
            y: isMobile ? e.clientY - 40 : e.clientY + 20, // Position above on mobile, below on desktop
        };
        lastMousePositionRef.current = position;
        setTooltipPosition(position);
    };

    // Add effect to manage body scroll
    useEffect(() => {
        // Add modal-open class to body when modal opens
        document.body.classList.add("modal-open");

        // Cleanup function when component unmounts
        return () => {
            // Remove modal-open class when modal closes
            document.body.classList.remove("modal-open");
        };
    }, []);

    // Update tooltip visibility when isVisible changes and cursor is already over image
    useEffect(() => {
        if (isVisible && isHoveringImage && !isMobile) {
            // Get current mouse position from lastMousePositionRef or use window's mouse position
            if (
                lastMousePositionRef.current.x === 0 &&
                lastMousePositionRef.current.y === 0 &&
                typeof window !== "undefined"
            ) {
                // Try to get the current mouse position from the global MouseEvent
                const mouseEvent = window.event as MouseEvent | undefined;
                if (mouseEvent) {
                    const position = {
                        x: mouseEvent.clientX,
                        y: isMobile
                            ? mouseEvent.clientY - 40
                            : mouseEvent.clientY + 20,
                    };
                    setTooltipPosition(position);
                } else {
                    setTooltipPosition(lastMousePositionRef.current);
                }
            } else {
                setTooltipPosition(lastMousePositionRef.current);
            }

            setShowImageTooltip(true);
        } else if (!isVisible && showImageTooltip) {
            // Ensure tooltip is hidden when component is not visible
            setShowImageTooltip(false);
        }
    }, [isVisible, isHoveringImage, isMobile]);

    // This function will be called when the component becomes visible with mouse already over it
    const detectMousePosition = useCallback(() => {
        // Use position from mouse move event listener
        const updatePosition = (e: MouseEvent) => {
            const position = {
                x: e.clientX,
                y: isMobile ? e.clientY - 40 : e.clientY + 20,
            };
            lastMousePositionRef.current = position;
            setTooltipPosition(position);
            // Remove the listener since we only need it once
            window.removeEventListener("mousemove", updatePosition);
        };

        // Listen for a single mouse move to get position
        window.addEventListener("mousemove", updatePosition, { once: true });
    }, [isMobile]);

    // When isVisible changes to true, try to detect mouse position
    useEffect(() => {
        if (isVisible && isHoveringImage) {
            detectMousePosition();
        }
    }, [isVisible, isHoveringImage, detectMousePosition]);

    return (
        <div className="project-modal-outer-container" ref={containerRef}>
            <div
                className={`project-modal-content-container ${
                    project.title === "Møvi.com" ? "project-movi" : ""
                }`}
            >
                <div className="project-modal-x-button-container">
                    <div
                        className="project-modal-x-button-outline"
                        onClick={onClose}
                    >
                        <div className="project-modal-x-button-inner">
                            <img
                                src={closeIcon}
                                alt="Close"
                                className="project-modal-x-button-icon"
                            />
                        </div>
                    </div>
                </div>
                <div className="project-modal-left-container">
                    <div
                        className="project-modal-image-container"
                        onMouseEnter={(e) => {
                            setIsHoveringImage(true);
                            const position = {
                                x: e.clientX,
                                y: isMobile ? e.clientY - 40 : e.clientY + 20,
                            };
                            lastMousePositionRef.current = position;
                            setTooltipPosition(position);
                            if (!isMobile && isVisible) {
                                setShowImageTooltip(true);
                            }
                        }}
                        onMouseLeave={() => {
                            setIsHoveringImage(false);
                            setShowImageTooltip(false);
                        }}
                        onMouseMove={handleMouseMove}
                        style={{
                            touchAction: "pan-y",
                            cursor: "pointer",
                        }}
                    >
                        <img
                            className={
                                "project-modal-left-container-image " +
                                (isFading ? "fade-out" : "fade-in")
                            }
                            src={displayedImage.path}
                            alt={project.title}
                            style={{ objectFit: displayedImage.objectFit }}
                            onClick={() =>
                                inFocus &&
                                onImageClick(
                                    displayedImage.path,
                                    displayedImage.objectFit,
                                    project.images[currentImageIndex]
                                        ?.descriptiveTitle
                                )
                            }
                            role="button"
                            onTouchMove={() => {}} // Empty handler instead of preventDefault
                        />
                        {showImageTooltip && isVisible && (
                            <Tooltip
                                text="Click to expand picture"
                                position={tooltipPosition}
                            />
                        )}
                    </div>
                </div>
                <div
                    className="project-modal-right-container"
                    ref={rightContainerRef}
                >
                    <div className="project-modal-right-container-title-and-text-container">
                        <div className="project-modal-right-container-title">
                            {project.title}
                        </div>
                        {matchingProject && (
                            <div className="project-modal-right-container-tech-container">
                                {matchingProject.tech_logos.map((logo) => (
                                    <div
                                        key={logo}
                                        onMouseEnter={() => {
                                            setShowTechTooltip(true);
                                            setActiveTechLogo(logo);
                                        }}
                                        onMouseLeave={() =>
                                            setShowTechTooltip(false)
                                        }
                                        onMouseMove={(e) =>
                                            setTooltipPosition({
                                                x: e.clientX,
                                                y: e.clientY + 20,
                                            })
                                        }
                                    >
                                        <img
                                            src={techLogoToPicture(logo)}
                                            alt={`${logo}`}
                                            className="project-modal-tech-logo"
                                        />
                                        {showTechTooltip &&
                                            activeTechLogo === logo && (
                                                <Tooltip
                                                    text={
                                                        logo
                                                            .charAt(0)
                                                            .toUpperCase() +
                                                        logo.slice(1)
                                                    }
                                                    position={tooltipPosition}
                                                />
                                            )}
                                    </div>
                                ))}
                            </div>
                        )}
                        <div className="project-modal-right-container-text">
                            {project.paragraphs.map((paragraph, index) => {
                                // Create a style for consistent spacing
                                const paragraphStyle = {
                                    marginBottom:
                                        index < project.paragraphs.length - 1
                                            ? "1.2em"
                                            : "0",
                                };

                                return (
                                    <div
                                        key={index}
                                        style={paragraphStyle}
                                        className="project-paragraph"
                                    >
                                        {typeof paragraph === "string" ? (
                                            // Render simple text paragraph
                                            paragraph === "" ? (
                                                <br />
                                            ) : (
                                                paragraph
                                            )
                                        ) : (
                                            // Render rich paragraph with links
                                            paragraph.segments.map(
                                                (segment, segmentIndex) => (
                                                    <React.Fragment
                                                        key={segmentIndex}
                                                    >
                                                        {segment.type ===
                                                        "text" ? (
                                                            segment.bold ? (
                                                                <strong>
                                                                    {
                                                                        segment.content
                                                                    }
                                                                </strong>
                                                            ) : (
                                                                segment.content
                                                            )
                                                        ) : (
                                                            <a
                                                                href={
                                                                    segment.link
                                                                        ?.url
                                                                }
                                                                target={
                                                                    segment.link
                                                                        ?.newTab !==
                                                                    false
                                                                        ? "_blank"
                                                                        : "_self"
                                                                }
                                                                rel="noopener noreferrer"
                                                                style={
                                                                    segment.bold
                                                                        ? {
                                                                              fontWeight:
                                                                                  "bold",
                                                                          }
                                                                        : {
                                                                              color: "white",
                                                                              textDecoration:
                                                                                  "underline",
                                                                          }
                                                                }
                                                            >
                                                                {
                                                                    segment.content
                                                                }
                                                            </a>
                                                        )}
                                                    </React.Fragment>
                                                )
                                            )
                                        )}
                                    </div>
                                );
                            })}
                            {!hasScrolled && isScrollable && (
                                <div className="project-modal-scroll-down-button">
                                    <p>You can scroll</p>
                                    <div className="scroll-down-arrow-container">
                                        <img
                                            src={arrowDown}
                                            alt="Scroll down"
                                            className="scroll-down-arrow"
                                        />
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default ProjectModal;
